import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { config } from '@/config';

/**
 * Gateway provisioning tokens.
 *
 * Core gateway endpoints, not MSP ones — safe to import directly, and
 * deliberately kept out of `@/features/msp` so nothing here has to be teased in
 * the trial build.
 *
 * The Gateways page still has its own inline copy of these calls. Unifying the
 * two is worth doing but is not this change's job; what matters here is that the
 * MSP surface does not add a *third*.
 */

const backendApi = config.backendApi;

export interface ProvisioningToken {
  id: string;
  /**
   * The raw token. Present in list responses as well as on create, so this is
   * not a create-only secret however much it reads like one.
   */
  token: string;
  organizationId: string;
  divisionId: string | null;
  gatewayType: string;
  status: string;
  name: string | null;
  description: string | null;
  createdAt: string;
  expiresAt: string;
  claimedAt: string | null;
  completedAt: string | null;
  failedAt: string | null;
  failureReason: string | null;
  /**
   * How many gateways it may add: 1, or null for a setup file's token, which
   * adds any number until it expires or is cancelled (docs/GatewaySetupFile.md,
   * slice 5).
   */
  maxUses: number | null;
  /** How many it has added. */
  uses: number;
}

/**
 * Whether a token can still add a gateway. A one-use token is spent once it has;
 * a setup file's is not, so having claimed doesn't end it.
 */
export function tokenIsLive(token: ProvisioningToken, now = new Date()): boolean {
  return token.status.toLowerCase() === 'pending' && new Date(token.expiresAt) > now;
}

/**
 * Every provisioning token the caller may see.
 *
 * Organization-wide with no division filter server-side, so callers narrow it
 * themselves. Note the list is filtered per row by permission rather than
 * refused outright — a short list does not mean few tokens exist.
 */
export function useProvisioningTokens(
  organizationId: string | null | undefined,
  enabled = true,
  /**
   * Poll while waiting on something to change a token's status — the Add
   * gateway flow watches for its freshly minted token to be claimed, which is
   * how it knows the gateway checked in.
   */
  refetchInterval?: number,
) {
  return useQuery<ProvisioningToken[]>({
    queryKey: ['provisioning-tokens', organizationId],
    queryFn: async () => {
      const response = await axios.get<{ tokens: ProvisioningToken[] }>(`${backendApi}/api/gateways/tokens`, {
        withCredentials: true,
      });
      return response.data.tokens;
    },
    enabled: enabled && !!organizationId,
    refetchInterval,
  });
}

export interface CreateProvisioningTokenInput {
  organizationId: string;
  divisionId: string | null;
  regionId: string;
  gateway_type: string;
  name: string | null;
  description: string | null;
  /** A setup file's token: adds any number of gateways until it expires. */
  unlimitedUses?: boolean;
  /** Its lifetime, one of SETUP_FILE_TOKEN_DAYS. Only with `unlimitedUses`. */
  expiresInDays?: number;
}

/** The lifetimes a setup file's token may have, in days (slice 5 ruling 2). */
export const SETUP_FILE_TOKEN_DAYS = [1, 7, 30, 90] as const;
export const SETUP_FILE_TOKEN_DEFAULT_DAYS = 7;

/**
 * Issues a token.
 *
 * Expiry is the server's to decide and differs by an order of magnitude between
 * gateway types — show what came back rather than predicting it.
 */
export function useCreateProvisioningToken() {
  const queryClient = useQueryClient();
  return useMutation<ProvisioningToken, Error, CreateProvisioningTokenInput>({
    mutationFn: async (input) => {
      const response = await axios.post<ProvisioningToken>(`${backendApi}/api/gateways/tokens`, input, {
        withCredentials: true,
      });
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['provisioning-tokens'] });
      // The Gateways page's own key, so its table doesn't go stale behind this.
      void queryClient.invalidateQueries({ queryKey: ['provisioningTokens'] });
    },
  });
}

/**
 * Cancels a token.
 *
 * Always an explicit act, never a side effect of issuing a new one: a token a
 * technician is halfway through using cannot be recovered once cancelled, and
 * the string itself is not re-derivable.
 */
export function useCancelProvisioningToken() {
  const queryClient = useQueryClient();
  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  return useMutation<void, Error, string>({
    mutationFn: async (tokenId) => {
      await axios.post(`${backendApi}/api/gateways/tokens/${tokenId}/cancel`, {}, { withCredentials: true });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['provisioning-tokens'] });
      void queryClient.invalidateQueries({ queryKey: ['provisioningTokens'] });
    },
  });
}
